<template>
  <div class="sal-page">
    <div class="sal-card">
      <div class="sal-card__header">
        <h1 class="sal-title">店家後台登入</h1>
        <p v-if="storeName" class="sal-store-name">歡迎，{{ storeName }}</p>
        <p v-else class="sal-subtitle">請使用店家管理員帳號登入</p>
      </div>

      <form class="sal-form" @submit.prevent="onSubmit">

        <div class="sal-field">
          <label for="sal-account">帳號</label>
          <input
            id="sal-account"
            v-model.trim="form.account"
            type="text"
            placeholder="請輸入帳號"
            class="sal-input"
            :class="{ 'sal-input--error': errors.account }"
            autocomplete="username"
          />
          <p v-if="errors.account" class="sal-field-error">{{ errors.account }}</p>
        </div>

        <div class="sal-field">
          <label for="sal-password">密碼</label>
          <div class="sal-password-wrap">
            <input
              id="sal-password"
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="請輸入密碼"
              class="sal-input"
              :class="{ 'sal-input--error': errors.password }"
              autocomplete="current-password"
            />
            <button
              type="button"
              class="sal-password-toggle"
              @click="showPassword = !showPassword"
              :aria-label="showPassword ? '隱藏密碼' : '顯示密碼'"
            >
              {{ showPassword ? '隱藏' : '顯示' }}
            </button>
          </div>
          <p v-if="errors.password" class="sal-field-error">{{ errors.password }}</p>
        </div>

        <!-- 登入失敗訊息 -->
        <div v-if="apiError" class="sal-api-error" role="alert">
          {{ apiError }}
        </div>

        <button
          type="submit"
          class="sal-submit"
          :disabled="submitting"
        >
          <span v-if="submitting" class="sal-spinner" />
          {{ submitting ? '登入中…' : '登入' }}
        </button>

      </form>

      <p class="sal-back">
        <router-link :to="{ name: 'StoreAdminForgotPassword' }">忘記密碼？</router-link>
        |
        <router-link :to="{ name: 'home' }">← 返回網站首頁</router-link>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStoreAdminStore } from '@/store/storeAdminStore'
import axios from 'axios'

const route           = useRoute()
const router          = useRouter()
const storeAdminStore = useStoreAdminStore()

const form = ref({ account: '', password: '' })
const errors = ref({ account: '', password: '' })
const apiError     = ref('')
const submitting   = ref(false)
const showPassword = ref(false)
const storeName    = ref('')

// 登入後根據是否新店家決定跳哪一頁
function redirectAfterLogin() {
  const obKey = `jili_ob_p_${storeAdminStore.storeId || ''}`
  if (!localStorage.getItem(obKey)) {
    router.replace({ name: 'StoreAdminProfile' })   // 新店家 → 直接步驟1 店家資料
  } else {
    router.replace({ name: 'StoreAdminGuide' })
  }
}

// 已登入就直接跳；若有 ?code= 則拉店家名稱
onMounted(async () => {
  const code = route.query.code
  if (code) {
    try {
      const { data } = await axios.get(`/api/store-admin/store-info?code=${code}`)
      if (data.success) storeName.value = data.store_name
    } catch { /* 靜默：無法取得店名不影響登入流程 */ }
  }
  if (!storeAdminStore.isLoggedIn) return
  const result = await storeAdminStore.fetchMe()
  if (result.success) {
    redirectAfterLogin()
  }
})

function validate() {
  errors.value = { account: '', password: '' }
  let ok = true
  if (!form.value.account) {
    errors.value.account = '請輸入帳號'
    ok = false
  }
  if (!form.value.password) {
    errors.value.password = '請輸入密碼'
    ok = false
  }
  return ok
}

async function onSubmit() {
  apiError.value = ''
  if (!validate()) return

  submitting.value = true
  try {
    const result = await storeAdminStore.login(form.value.account, form.value.password)
    if (result.success) {
      await storeAdminStore.fetchMe()
      redirectAfterLogin()
    } else {
      // 顯示後端回傳的失敗訊息（帳號或密碼錯誤 / 帳號已停用等）
      apiError.value = result.message || '登入失敗，請確認帳號與密碼'
    }
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.sal-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f6f9;
  padding: 24px 16px;
}

.sal-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10);
  padding: 40px 36px;
  width: 100%;
  max-width: 420px;
}

.sal-card__header {
  text-align: center;
  margin-bottom: 32px;
}
.sal-title {
  font-size: 1.5rem;
  color: #2c3e50;
  margin: 0 0 8px;
}
.sal-subtitle {
  font-size: 0.88rem;
  color: #95a5a6;
  margin: 0;
}
.sal-store-name {
  font-size: 1rem;
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
}

/* 表單 */
.sal-form { display: flex; flex-direction: column; gap: 20px; }

.sal-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sal-field label {
  font-size: 0.88rem;
  color: #555;
  font-weight: 500;
}
.sal-input {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid #dce1e7;
  border-radius: 8px;
  font-size: 1rem;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s;
  -webkit-appearance: none;
}
.sal-input:focus {
  outline: none;
  border-color: #2c3e50;
  box-shadow: 0 0 0 3px rgba(44,62,80,0.08);
}
.sal-input--error { border-color: #e74c3c; }
.sal-field-error {
  font-size: 0.8rem;
  color: #e74c3c;
  margin: 0;
}

.sal-password-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.sal-password-wrap .sal-input {
  padding-right: 64px;
}
.sal-password-toggle {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  font-size: 0.8rem;
  color: #95a5a6;
  cursor: pointer;
  padding: 0;
}
.sal-password-toggle:hover { color: #555; }

/* API 錯誤 */
.sal-api-error {
  background: #fdedec;
  border: 1px solid #f5b7b1;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.88rem;
  color: #c0392b;
  line-height: 1.5;
}

/* 送出按鈕 */
.sal-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 13px;
  background: #2c3e50;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  margin-top: 4px;
}
.sal-submit:hover:not(:disabled) { background: #34495e; }
.sal-submit:disabled { opacity: 0.6; cursor: not-allowed; }

/* Spinner */
.sal-spinner {
  display: inline-block;
  width: 18px; height: 18px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* 返回連結 */
.sal-back {
  text-align: center;
  margin-top: 20px;
  font-size: 0.85rem;
}
.sal-back a {
  color: #95a5a6;
  text-decoration: none;
}
.sal-back a:hover { color: #555; text-decoration: underline; }

@media (max-width: 480px) {
  .sal-card { padding: 28px 20px; }
  .sal-title { font-size: 1.3rem; }
}
</style>
